// StudentSpellCheck.tsx
import { useState, useEffect, useRef } from 'react';
import FireworksLayer from '@/components/interAction/FireworksLayer';
import ListenButton from '@/components/atoms/ListenButton/ListenButton'
import FlipCard from '@/components/interAction/FlipCard'
import * as styles from '@/components/organisms/Learning/LearningSytle.css'
import * as spellStyles from './SpellCheckStyle.css'
import * as char from '@/components/interAction/Drag/DragStyle.css'
import appleImg from '@/assets/img/nol_list.png'
import { getStudentActivityTitle } from '@/utils/activityTitle';

interface SpellCheckProps {
  activity?: any;
  unitData?: any;
  onResponse?: (response: any) => void;
  existingResponse?: any;
  isHomework?: boolean;
}

function shuffleArray<T>(array: T[]): T[] {
    const arr = [...array]
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
    }
    return arr
  }

const LETTER_POOL = 'abcdefghijklmnopqrstuvwxyz'.split('');
const EMPTY_PLACEHOLDER = '_';

const generateFakeLetterIds = (count: number, excludedLetters: string[]): string[] => {
  if (count <= 0) return [];

  const excludedSet = new Set(excludedLetters.map((ch) => ch.toLowerCase()));
  let available = LETTER_POOL.filter((ch) => !excludedSet.has(ch));
  if (available.length === 0) {
    available = [...LETTER_POOL];
  }

  const fakeIds: string[] = [];
  const usedLetters = new Set<string>();

  for (let i = 0; i < count; i++) {
    let letter: string;
    let attempts = 0;
    do {
      letter = available[Math.floor(Math.random() * available.length)];
      attempts += 1;
      if (attempts > available.length * 2) {
        break;
      }
    } while (usedLetters.has(letter) && usedLetters.size < available.length);

    usedLetters.add(letter);
    fakeIds.push(`${letter}-fake-${i}`);
  }

  return fakeIds;
};

export default function StudentSpellCheck({ activity, unitData, onResponse, existingResponse, isHomework = false }: SpellCheckProps) {
  console.log('=== SpellCheck 시작 ===');
  console.log('Activity:', activity);
  console.log('isHomework:', isHomework);
  
  // activity.items에서 실제 데이터 가져오기
  const activityData = activity?.items?.[0] || {};
  let content: any = {};
  
  try {
    const contentJson = activityData.contentJson;
    if (contentJson) {
      content = typeof contentJson === 'string' ? JSON.parse(contentJson) : contentJson;
      console.log('Parsed contentJson:', content);
    }
  } catch (e) {
    console.error('contentJson 파싱 에러:', e);
  }

  const word = content.word || content.text || 'apple';
  const hasImage = !!(content.wordImageUrl || content.imageUrl || content.image);
  const imageUrl = hasImage ? (content.wordImageUrl || content.imageUrl || content.image) : appleImg;
  const audioUrl = content.audioUrl || content.audio || null;
  const title = getStudentActivityTitle(activity?.title, '철자 맞추기');
  
  console.log('=== SpellCheck 파싱 결과 ===');
  console.log('Word:', word);
  console.log('ImageUrl:', imageUrl);
  console.log('AudioUrl:', audioUrl);

  const correctAnswer = word;
  const correctLetters = correctAnswer.split('');
  
  // 단어 길이에 따라 보일 글자 개수 결정 + 중복 문자 고려한 랜덤 위치 선택
  const getVisibleIndices = (letters: string[]): number[] => {
    const wordLength = letters.length;
    let visibleCount = 0;
    
    // 단어 길이별 보여질 글자 수
    if (wordLength === 2) {
      visibleCount = 0; // 전부 가림
    } else if (wordLength === 3) {
      visibleCount = 1; // 한 글자만 보임
    } else if (wordLength >= 4 && wordLength <= 6) {
      visibleCount = 1; // 한 글자만 보임
    } else if (wordLength >= 7) {
      visibleCount = 2; // 두 글자 보임
    }

    if (visibleCount <= 0) return [];

    // 중복 문자 고려: 각 고유 문자별로 인덱스 그룹화
    const letterGroups = new Map<string, number[]>();
    letters.forEach((ch, idx) => {
      const key = ch.toLowerCase();
      const indices = letterGroups.get(key) || [];
      indices.push(idx);
      letterGroups.set(key, indices);
    });

    // 고유 문자 목록
    const uniqueLetters = Array.from(letterGroups.keys());
    
    // 랜덤으로 섞기
    const shuffledLetters = [...uniqueLetters].sort(() => Math.random() - 0.5);
    
    const selected: number[] = [];

    // 각 고유 문자 그룹에서 하나씩 선택 (중복되지 않게)
    for (const letter of shuffledLetters) {
      if (selected.length >= visibleCount) break;
      
      const indices = letterGroups.get(letter)!;
      // 해당 문자의 인덱스 중 랜덤으로 하나 선택
      const randomIndex = indices[Math.floor(Math.random() * indices.length)];
      selected.push(randomIndex);
    }

    return selected.sort((a, b) => a - b);
  };
  
  // 이전 응답이 있으면 저장된 visibleIndices 사용, 없으면 한 번만 생성해 고정
  const [visibleIndices] = useState<number[]>(
    () => existingResponse?.visibleIndices || getVisibleIndices(correctLetters)
  );
  const blankIndices = correctLetters
    .map((_: string, idx: number) => idx)
    .filter((idx: number) => !visibleIndices.includes(idx));
  
  console.log('=== SpellCheck 데이터 ===');
  console.log('원문자:', word);
  console.log('단어 길이:', correctLetters.length);
  console.log('보이는 글자 인덱스:', visibleIndices);
  console.log('보이는 글자:', visibleIndices.map((i: number) => correctLetters[i]).join(', '));
  console.log('가려진 글자 인덱스:', blankIndices);
  console.log('가려진 글자:', blankIndices.map((i: number) => correctLetters[i]).join(', '));
  
  // 글자 ID 생성 (빈칸 글자만, 중복 글자 처리)
  const blankLetterIds = blankIndices.map((idx: number) => {
    const letter = correctLetters[idx];
    const count = correctLetters.slice(0, idx).filter((l: string) => l === letter).length;
    return `${letter}-${count}`;
  });

  const fakeLetterIds = generateFakeLetterIds(
    blankLetterIds.length,
    correctLetters
  );

  const allLetters = shuffleArray([...blankLetterIds, ...fakeLetterIds]);

  // 초기 answer 배열 생성 (보이는 글자는 미리 채워둠)
  const getInitialAnswer = () => {
    const initialAnswer = new Array(correctLetters.length).fill('');
    visibleIndices.forEach((idx: number) => {
      const letter = correctLetters[idx];
      const count = correctLetters.slice(0, idx).filter((l: string) => l === letter).length;
      initialAnswer[idx] = `${letter}-${count}`;
    });
    return initialAnswer;
  };

  // existingResponse에서 이전 답변 복원
  const getInitialState = (baseAnswer: string[]) => {
    const initialAnswer = [...baseAnswer];
    
    if (existingResponse?.answerState && Array.isArray(existingResponse.answerState) && existingResponse.answerState.length === correctLetters.length) {
      const storedAnswer = existingResponse.answerState.map((value: string | null, idx: number) => {
        if (typeof value === 'string' && value.length > 0) {
          return value;
        }
        return initialAnswer[idx];
      });
      const usedLetters: string[] = [];
      blankIndices.forEach((blankIdx: number) => {
        const storedId = storedAnswer[blankIdx];
        if (storedId) {
          usedLetters.push(storedId);
        }
      });
      const remainingLetters = allLetters.filter((id) => !usedLetters.includes(id));
      let progress = blankIndices.length;
      for (let i = 0; i < blankIndices.length; i++) {
        const blankIdx = blankIndices[i];
        if (!storedAnswer[blankIdx]) {
          progress = i;
          break;
        }
      }
      return {
        answer: storedAnswer,
        choiceLetters: remainingLetters,
        currentBlankIndex: progress,
      };
    }
    
    if (existingResponse?.userAnswer) {
      console.log('SpellCheck 이전 답변 복원:', existingResponse);
      console.log('원문자:', existingResponse.originalWord);
      console.log('보였던 위치:', existingResponse.visibleIndices);
      console.log('사용자 답변:', existingResponse.userAnswer);
      
      const previousAnswer = existingResponse.userAnswer;
      const previousAnswerArray = previousAnswer.split(''); // 문자열을 배열로 변환
      
      // 이전 답변을 answer 배열로 복원
      const restoredAnswer = [...initialAnswer];
      const usedLetters: string[] = [];
      
      // 사용자가 입력했던 문자들을 복원
      blankIndices.forEach((blankIdx: number) => {
        const letter = previousAnswerArray[blankIdx];
        if (letter && letter !== EMPTY_PLACEHOLDER) {
          const count = previousAnswerArray.slice(0, blankIdx).filter((l: string) => l === letter).length;
          const letterId = `${letter}-${count}`;
          restoredAnswer[blankIdx] = letterId;
          usedLetters.push(letterId);
        }
      });
      
      // 사용된 글자를 제외한 나머지 글자들
      const remainingLetters = allLetters.filter(id => !usedLetters.includes(id));
      
      return {
        answer: restoredAnswer,
        choiceLetters: remainingLetters,
        currentBlankIndex: usedLetters.length
      };
    }
    
    return {
      answer: initialAnswer,
      choiceLetters: allLetters,
      currentBlankIndex: 0
    };
  };
  
  const baseAnswer = getInitialAnswer();
  const initialState = getInitialState(baseAnswer);
  
  const [choiceLetters, setChoiceLetters] = useState<string[]>(initialState.choiceLetters);
  const [answer, setAnswer] = useState<string[]>(initialState.answer);
  const [showFireworks, setShowFireworks] = useState(false)
  const [flipStatus, setFlipStatus] = useState<'idle' | 'success' | 'fail'>('idle')
  const [currentBlankIndex, setCurrentBlankIndex] = useState(initialState.currentBlankIndex) // 현재 입력 중인 빈칸의 인덱스 (blankIndices 배열 기준)
  const [imageLoadError, setImageLoadError] = useState(false); // 이미지 로드 에러 상태
  const isInitialMount = useRef(true); // 초기 마운트 여부 추적
  const isLocked = !isHomework && Boolean(existingResponse?.isCorrect);

  // 마운트 시 초기 응답 저장
  useEffect(() => {
    if (onResponse && !existingResponse) {
      onResponse({ 
        originalWord: word,
        visibleIndices: visibleIndices,
        userAnswer: ''.padEnd(correctLetters.length, EMPTY_PLACEHOLDER),
        isCorrect: false,
        answerState: baseAnswer,
        isHomeworkReady: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isAllFilled =
      answer.length === correctLetters.length && answer.every(Boolean);
  
    // 진행 중 답변도 저장 (이전 버튼으로 돌아올 때 복원용)
    if (onResponse) {
      const userAnswerString = answer
        .map((v) => (v ? v.split('-')[0] : EMPTY_PLACEHOLDER))
        .join('');
      const cleanedUserAnswer = userAnswerString.split(EMPTY_PLACEHOLDER).join('');
      const isCorrect = isAllFilled && userAnswerString === correctLetters.join('');
      
      onResponse({ 
        originalWord: word,
        visibleIndices: visibleIndices,
        userAnswer: userAnswerString,
        isCorrect: isAllFilled && cleanedUserAnswer === correctLetters.join(''),
        answerState: answer,
        isHomeworkReady: isAllFilled
      });
    }
    
    if (!isAllFilled) return;
  
    const userAnswerString = answer
      .map((v) => (v ? v.split('-')[0] : EMPTY_PLACEHOLDER))
      .join('');
    const cleanedUserAnswer = userAnswerString.split(EMPTY_PLACEHOLDER).join('');
    const isCorrect = cleanedUserAnswer === correctLetters.join('');
  
    // 초기 마운트 시 이미 완성된 답변으로 복원된 경우에만 애니메이션 스킵
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // existingResponse가 있고 이미 정답을 맞춘 상태로 복원된 경우에만 애니메이션 스킵
      if (existingResponse?.userAnswer && existingResponse.userAnswer === correctLetters.join('')) {
        return;
      }
    }
  
    if (isCorrect) {
      // 학습 모드: 피드백만 표시하고 답변 유지 (이전 버튼으로 돌아올 수 있도록)
      if (!isHomework) {
        setFlipStatus('success');
        setShowFireworks(true);
        
        // 폭죽 효과만 제거하고 답변은 유지
        setTimeout(() => {
          setShowFireworks(false);
          setFlipStatus('idle');
        }, 2000);
      }
    } else {
      // 숙제가 아닐 때만 피드백 표시
      if (!isHomework) {
        setFlipStatus('fail');
      }
  
      // 숙제인 경우 초기화하지 않고 그대로 유지
      if (!isHomework) {
        setTimeout(() => {
          resetGame();
          setFlipStatus('idle');
        }, 1500);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer]);

  // 선택지 알파벳 클릭 핸들러 (빈칸에 채우기)
  const handleLetterClick = (letterId: string) => {
    if (isLocked) return;
    if (currentBlankIndex >= blankIndices.length) return; // 모든 빈칸이 채워진 경우
    
    const targetIndex = blankIndices[currentBlankIndex]; // 실제 단어에서의 인덱스
    const newAnswer = [...answer];
    newAnswer[targetIndex] = letterId;
    setAnswer(newAnswer);
    
    // 사용된 알파벳을 choiceLetters에서 제거
    setChoiceLetters(prev => prev.filter(id => id !== letterId));
    
    // 다음 빈칸으로 이동
    setCurrentBlankIndex((prev: number) => prev + 1);
  };

  // 이미 채워진 글자 클릭 핸들러 (다시 선택지로 돌려보내기)
  const handleAnswerSlotClick = (idx: number) => {
    if (isLocked) return;
    const letterId = answer[idx];
    if (!letterId) return; // 빈칸이면 무시
    if (visibleIndices.includes(idx)) return; // 보이는 글자는 제거 불가
    
    // answer에서 해당 위치를 빈칸으로
    const newAnswer = [...answer];
    newAnswer[idx] = '';
    setAnswer(newAnswer);
    
    // choiceLetters에 다시 추가 (중복 방지)
    setChoiceLetters(prev => {
      if (prev.includes(letterId)) {
        return prev;
      }
      return [...prev, letterId];
    });
    
    // currentBlankIndex를 가장 앞의 빈칸으로 설정 (순서대로 채우기 위해)
    let firstEmptyBlankIndex = 0;
    for (let i = 0; i < blankIndices.length; i++) {
      const blankIdx = blankIndices[i];
      if (!newAnswer[blankIdx]) {
        firstEmptyBlankIndex = i;
        break;
      }
    }
    setCurrentBlankIndex(firstEmptyBlankIndex);
  };
  
  const resetGame = () => {
    const resetLetters = shuffleArray(allLetters);
    setChoiceLetters(resetLetters);
    setAnswer(getInitialAnswer()); // 보이는 글자는 다시 채워둠
    setFlipStatus('idle');
    setShowFireworks(false);
    setCurrentBlankIndex(0);
  }

  return (
    <main>
      <h1 className="px-5 pt-5 heading1">{title}</h1>
      <div className="p-5 pt-3">
        <div>
          <FlipCard
            flipStatus={flipStatus}
            front={
              <div className={styles.gameCard}>
                <div className='relative size-[150px] border border-gray-100 flex items-center justify-center rounded-2xl overflow-hidden bg-gray-50'>
                  {hasImage && !imageLoadError ? (
                    <img 
                      src={imageUrl} 
                      alt={word} 
                      className="object-cover size-full"
                      onError={() => {
                        setImageLoadError(true);
                      }}
                    />
                  ) : (
                    <div className="flex flex-col justify-center items-center text-sm text-gray-400 size-full">
                      <img src={appleImg} alt="no list" className="size-20" />
                      <p className="text-sm text-gray-400">이미지가 없습니다.</p>
                    </div>
                  )}
                </div>
                <ListenButton size={48} className="absolute top-4 right-4" audioSrc={audioUrl} />
                <section className={styles.listWrapper}>
                  {correctLetters.map((_: string, idx: number) => {
                    const letterId = answer[idx];
                    const isVisible = visibleIndices.includes(idx); // 미리 보이는 글자인지
                    const isBlank = blankIndices.includes(idx); // 빈칸인지
                    const blankOrder = blankIndices.indexOf(idx); // 몇 번째 빈칸인지
                    const isCurrentSlot = isBlank && blankOrder === currentBlankIndex; // 현재 입력 중인 빈칸인지
                    const isCorrect = !isHomework && letterId && letterId.split('-')[0] === correctLetters[idx];
                    const isWrong = !isHomework && letterId && letterId.split('-')[0] !== correctLetters[idx];
                    
                    return (
                      <div key={idx} className="relative">
                        <div 
                          onClick={() => handleAnswerSlotClick(idx)}
                          className={`${spellStyles.answerSlot} ${
                            isVisible ? '!bg-white text-gray-900 !shadow-none' : '' // 보이는 글자는 회색 배경
                          } ${
                            !isHomework && isCurrentSlot && !letterId ? spellStyles.currentSlot : ''
                          } ${
                            isCorrect ? spellStyles.correctAnswer : ''
                          } ${
                            isWrong ? spellStyles.wrongAnswer : ''
                          } ${
                            letterId && !isVisible ? 'cursor-pointer hover:opacity-70' : '' // 채워진 글자는 클릭 가능
                          }`}
                        >
                          {letterId ? letterId.split('-')[0] : (isBlank ? '' : '')}
                        </div>
                        {!isHomework && isCurrentSlot && !letterId && (
                          <div className="absolute inset-0 rounded-lg border-2 border-yellow-400 border-dashed animate-pulse"></div>
                        )}
                      </div>
                    );
                  })}
                </section>
              </div>
            }
          />
        </div>
        
        {/* 알파벳 선택 영역 */}
        <section className="mt-5">
          <div className="flex flex-wrap gap-2 justify-center items-center p-6 w-full bg-gray-50 rounded-2xl min-h-32">
            {choiceLetters.map((id: string) => {
              const letter = id.split('-')[0];
              
              return (
                <button
                  key={id}
                  onClick={() => handleLetterClick(id)}
                  disabled={isLocked}
                  className={char.charSelect}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </section>
        {showFireworks && <FireworksLayer />}
      </div>
    </main>
  )
}
